import { useState, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { http, formatError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PencilSimple, Plus, Image as ImageIcon } from "@phosphor-icons/react";

const CATEGORIES = ["Investment Education", "Financial Literacy", "Real Estate", "Agriculture", "Business", "Mayoto News"];

const slugify = (t) => (t || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 60);

export default function BlogEditorDialog({ post, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(post || {
    title: "", slug: "", author: "Mayoto Editorial", category: "Investment Education",
    reading_time: "5 min read", excerpt: "", content: "", image_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadImage = (file) => new Promise(async (resolve, reject) => {
    try {
      if (!file) return reject("no file");
      if (file.size > 3 * 1024 * 1024) return reject("Image too large — max 3MB");
      setUploading(true);
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await http.post("/admin/blog/upload-image", { image_b64: b64 });
      const url = `${BACKEND_URL}${data.url}`;
      resolve(url);
    } catch (e) { reject(e); }
    finally { setUploading(false); }
  });

  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      try {
        const url = await uploadImage(file);
        const editor = quillRef.current?.getEditor?.();
        const range = editor?.getSelection(true);
        editor.insertEmbed(range ? range.index : 0, "image", url);
      } catch (e) { toast.error(String(e)); }
    };
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      set("image_url", url);
      toast.success("Cover image uploaded");
    } catch (er) { toast.error(String(er)); }
  };

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
  };

  const save = async () => {
    if (!form.title || !form.content) { toast.error("Title and content are required"); return; }
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    setLoading(true);
    try {
      if (post?.id) { await http.put(`/admin/blog/${post.id}`, payload); toast.success("Post updated"); }
      else { await http.post("/admin/blog", payload); toast.success("Post published"); }
      setOpen(false);
      onSaved?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button className="bg-[#00b4d8] hover:bg-[#0096c7] rounded-full" data-testid="blog-new-btn"><Plus size={16} className="mr-1" />New Post</Button>}</DialogTrigger>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{post ? "Edit Post" : "New Blog Post"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-5 pt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => { set("title", e.target.value); if (!form.slug) set("slug", slugify(e.target.value)); }} data-testid="blog-title-input" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={e => set("slug", slugify(e.target.value))} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Author</Label><Input value={form.author} onChange={e => set("author", e.target.value)} /></div>
            <div><Label>Reading Time</Label><Input value={form.reading_time} onChange={e => set("reading_time", e.target.value)} /></div>
            <div>
              <Label>Cover Image</Label>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" onChange={uploadCover} className="hidden" id="blog-cover" data-testid="blog-cover-input" />
                <label htmlFor="blog-cover" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-[#00b4d8] border border-slate-200 rounded-lg px-3 py-2"><ImageIcon size={16} />{uploading ? "Uploading…" : "Upload cover"}</label>
                {form.image_url && <img src={form.image_url} alt="cover" className="h-10 w-16 object-cover rounded-md border" />}
              </div>
            </div>
          </div>
          <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={e => set("excerpt", e.target.value)} /></div>
          <div>
            <Label>Content</Label>
            <div className="mt-1 border border-slate-200 rounded-xl bg-white">
              <ReactQuill ref={quillRef} theme="snow" value={form.content} onChange={v => set("content", v)} modules={modules} data-testid="blog-content-editor" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={loading} className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7]" data-testid="blog-save-btn">{loading ? "Saving…" : post ? "Update" : "Publish"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
